// Third Party
import React, { Component } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { connect } from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import {
  loadGifPortfolioForEdit,
  createNewScenario,
  cancelScenarioEditing,
  saveScenario,
  setLumpSumWithdrawalScheme,
  setLumpSumWithdrawalAmount,
  setLumpSumWithdrawalAt,
  setSystematicWithdrawalAmount,
  setSystematicWithdrawalType,
  setSystematicWithdrawalScheme,
  setSystematicWithdrawalStart,
  setSystematicWithdrawalEnd,
  setSystematicWithdrawalYears,
  setSystematicWithdrawalFrequency,
  loadScenarioForEdit,
  deleteScenario,
  selectScenario,
  setLumpSumWithdrawalMonth,
  setSystematicWithdrawalMonthEnd,
  setSystematicWithdrawalMonthStart,
} from '../../../../redux/modules/bmo-gif';

// Components
import BMOGIFComparisonTableLabels from './BMOGIFComparisonTableLabels';
import BMOGIFComparisonCard from './BMOGIFComparisonCard';
import { withErrorBoundary } from '../../../components/errors';
import { GenericEmptyState } from '../../../components/misc';

// Utils
import { formatMessage } from '../../../../utils/i18n';
import { getTotalGifPortfolioAllocation } from '../../../../utils/bmo-gif/index';
import { BMOi18n } from '../../../../utils/bmo-gif/constants';
import { UNIT_BASE } from '../../../../utils/constants';
import { comparisonHasGifItems } from '../../../../utils/comparison';
import { getAnnualData } from '../../../../utils/bmo-gif/utils';
import Comparison from '@/types/comparison';
import ComparisonCategory from '@/types/comparison-category';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import BMOGIFScenarioData from '@/types/bmo-gif/bmo-gif-scenario-data';

type StateProps = {
  comparison: Comparison;
  editedGifItem?: BMOGIFItem;
  portfolio?: BMOGIFPortfolio;
  locale: string;
  editedScenario?: BMOGIFScenario;
  readOnly?: boolean;
  selectedScenario?: BMOGIFScenario;
  showGraph: boolean;
  accountType: string;
};

type DispatchProps = {
  loadGifPortfolioForEdit: (gifItem: BMOGIFItem) => void;
  createNewScenario: () => void;
  cancelScenarioEditing: () => void;
  saveScenario: () => void;
  setLumpSumWithdrawalScheme: (scheme: string) => void;
  setLumpSumWithdrawalAmount: (amount: number) => void;
  setLumpSumWithdrawalAt: (at: number) => void;
  setSystematicWithdrawalAmount: (amount: number) => void;
  setSystematicWithdrawalType: (type: string) => void;
  setSystematicWithdrawalScheme: (scheme: string) => void;
  setSystematicWithdrawalStart: (start?: number) => void;
  setSystematicWithdrawalEnd: (end?: number) => void;
  setSystematicWithdrawalYears: (years?: number) => void;
  setSystematicWithdrawalFrequency: (frequency: string) => void;
  loadScenarioForEdit: (scenario: BMOGIFScenario) => void;
  deleteScenario: (scenario: BMOGIFScenario) => void;
  selectScenario: (scenario: BMOGIFScenario) => void;
  setLumpSumWithdrawalMonth: (month?: number) => void;
  setSystematicWithdrawalMonthEnd: (month?: number) => void;
  setSystematicWithdrawalMonthStart: (month?: number) => void;
};

type Props = StateProps & DispatchProps;

const EDIT_SCENARIO_CARD_WIDTH = 47 * UNIT_BASE;

class BMOGIFComparisonTable extends Component<Props> {
  componentDidMount() {
    const { comparison, editedGifItem } = this.props;
    // Always load first GIF item
    const gifItem = comparison.gifItems && comparison.gifItems.length && (comparison.gifItems[0] as BMOGIFItem);
    if (gifItem && !editedGifItem) {
      this.props.loadGifPortfolioForEdit(gifItem);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { editedScenario } = this.props;

    if (!editedScenario) {
      return;
    }

    if (!prevProps.editedScenario || (editedScenario._id && prevProps.editedScenario._id !== editedScenario._id)) {
      this.focusScenarioEdit();
    }
  }

  focusScenarioEdit = () => {
    const cardsContainer = document.getElementsByClassName('bmo-gif-comparison-cards')[0] as HTMLElement;
    const target = document.getElementsByClassName('withdrawal-scenario editing-mode')[0] as HTMLElement;

    if (cardsContainer && cardsContainer.scrollTo) {
      const { offsetLeft, scrollWidth } = cardsContainer;

      const scrollLeft = target ? target.offsetLeft - EDIT_SCENARIO_CARD_WIDTH : scrollWidth - offsetLeft;

      setTimeout(() => {
        cardsContainer.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }, 100);
    }
  };

  getBMOCategories = (): ComparisonCategory[] => {
    const { editedGifItem } = this.props;
    const data = !!editedGifItem && !!editedGifItem.baseScenario && editedGifItem.baseScenario.data;
    const purchaseDate = (!!editedGifItem && editedGifItem.baseScenario.purchaseDate) || new Date();

    const fields =
      data &&
      getAnnualData(data, purchaseDate).map((scenarioData: BMOGIFScenarioData) => ({
        key: 'date',
        label: moment(scenarioData.date).utc().format(formatMessage('Common.DateFormats.Long')),
      }));

    return [
      {
        key: 'years',
        label: 'Date',
        fields: fields || [],
      },
    ];
  };

  itemIsBeingEdited = (item: BMOGIFItem) => !!this.props.editedGifItem && this.props.editedGifItem._id === item._id;

  renderCard = (item: BMOGIFItem) => {
    const {
      editedGifItem,
      locale,
      editedScenario,
      portfolio,
      readOnly,
      selectedScenario,
      showGraph,
      comparison: { contact },
    } = this.props;

    const isEdited = this.itemIsBeingEdited(item);
    return (
      <BMOGIFComparisonCard
        accountType={contact && contact.accountType}
        key={item._id}
        item={(isEdited && editedGifItem) || item}
        locale={locale}
        portfolio={portfolio}
        readOnly={readOnly}
        createNewScenario={this.props.createNewScenario}
        editedScenario={editedScenario}
        saveScenario={this.props.saveScenario}
        cancelScenarioEditing={this.props.cancelScenarioEditing}
        setLumpSumWithdrawalScheme={this.props.setLumpSumWithdrawalScheme}
        setLumpSumWithdrawalAmount={this.props.setLumpSumWithdrawalAmount}
        setLumpSumWithdrawalAt={this.props.setLumpSumWithdrawalAt}
        setSystematicWithdrawalAmount={this.props.setSystematicWithdrawalAmount}
        setSystematicWithdrawalType={this.props.setSystematicWithdrawalType}
        setSystematicWithdrawalScheme={this.props.setSystematicWithdrawalScheme}
        setSystematicWithdrawalStart={this.props.setSystematicWithdrawalStart}
        setSystematicWithdrawalEnd={this.props.setSystematicWithdrawalEnd}
        setSystematicWithdrawalYears={this.props.setSystematicWithdrawalYears}
        setSystematicWithdrawalFrequency={this.props.setSystematicWithdrawalFrequency}
        loadScenarioForEdit={this.props.loadScenarioForEdit}
        deleteScenario={this.props.deleteScenario}
        selectScenario={this.props.selectScenario}
        selectedScenario={selectedScenario}
        showGraph={showGraph}
        setLumpSumMonth={this.props.setLumpSumWithdrawalMonth}
        setSystematicWithdrawalMonthEnd={this.props.setSystematicWithdrawalMonthEnd}
        setSystematicWithdrawalMonthStart={this.props.setSystematicWithdrawalMonthStart}
      />
    );
  };

  render() {
    const { comparison, portfolio, locale, ...rest } = this.props;

    if (!portfolio || !comparison.contact) {
      return null;
    }

    const portfolioIsFull = getTotalGifPortfolioAllocation(portfolio) === 100;
    const hasGifItems = comparisonHasGifItems(comparison);

    return (
      <div className={classNames('bmo-gif-comparison-table d-flex', { 'hide-content': !portfolioIsFull })}>
        {!hasGifItems && <GenericEmptyState header={BMOi18n[locale].SCENARIOS.EMPTY_STATE_TITLE} body={BMOi18n[locale].SCENARIOS.EMPTY_STATE_MESSAGE} />}

        {hasGifItems && (
          <>
            <BMOGIFComparisonTableLabels categories={this.getBMOCategories()} />

            <PerfectScrollbar
              className="bmo-gif-comparison-cards"
              // Enable horizontal scroll naturally only on touch devices -- Gives best UX this way.
              options={{ suppressScrollY: true }}
            >
              {comparison.gifItems && comparison.gifItems.map(this.renderCard)}

              <div className="table-padding" />
            </PerfectScrollbar>

            {!portfolioIsFull && (
              <div className="hide-content-overlay">
                <GenericEmptyState
                  headerIcon="icon-error"
                  header={BMOi18n[locale].HIDE_CONTENT_OVERLAY.TITLE}
                  body={BMOi18n[locale].HIDE_CONTENT_OVERLAY.MESSAGE}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  portfolio: state.bmoGif.portfolio,
  editedGifItem: state.bmoGif.gifItem,
  locale: state.intl.locale,
  editedScenario: state.bmoGif.editedScenario,
  selectedScenario: state.bmoGif.selectedScenario,
  showGraph: state.bmoGif.showGraph,
});
export default withErrorBoundary(
  connect(mapStateToProps, {
    loadGifPortfolioForEdit,
    createNewScenario,
    cancelScenarioEditing,
    saveScenario,
    setLumpSumWithdrawalScheme,
    setLumpSumWithdrawalAmount,
    setLumpSumWithdrawalAt,
    setSystematicWithdrawalAmount,
    setSystematicWithdrawalType,
    setSystematicWithdrawalScheme,
    setSystematicWithdrawalStart,
    setSystematicWithdrawalEnd,
    setSystematicWithdrawalYears,
    setSystematicWithdrawalFrequency,
    loadScenarioForEdit,
    deleteScenario,
    selectScenario,
    setLumpSumWithdrawalMonth,
    setSystematicWithdrawalMonthEnd,
    setSystematicWithdrawalMonthStart,
  })(BMOGIFComparisonTable),
);
